import * as React from 'react';
import {
  Button,
  DatePicker,
  DatePickerPropsBase,
  DatePickerScrollableDialogContent,
  Dialog,
  DialogActions,
  DialogHeading,
  IconButton,
  Popover,
  PopoverProps,
} from 'omakase';
import { FormatDateOptions, useIntl } from 'react-intl';
import { IconArrowDownSmall, IconCalendar as IconCalendarLegacy } from 'ui/icons';
import { IconCalendar, IconChevronSmallDown } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { InputAdornment } from '@material-ui/core';
import { LocalDate } from 'js-joda';
import { TextField, TextFieldProps } from 'ui';
import { Theme, css } from '@emotion/react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { toJSDate } from 'utils/timeUtils';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import _ from 'lodash';
import clsx from 'clsx';

export const useDatePickerStyles = makeStyles((theme) => ({
  input: {
    '& input': {
      cursor: 'pointer',
    },

    '& .MuiInputBase-adornedEnd': {
      paddingRight: 4,
    },
    '& .MuiInputBase-input[disabled]': {
      color: theme.colors.fgDefault,
      '-webkit-text-fill-color': theme.colors.fgDefault,
      opacity: 1,
    },
    '& .MuiButtonBase-root': {
      paddingRight: theme.spacing(1),
    },
  },
}));

export interface DatePickerFieldProps
  extends Pick<TextFieldProps, Exclude<keyof TextFieldProps, 'onChange'>> {
  'aria-label'?: string;
  PopoverProps?: Partial<PopoverProps>;
  DatePickerProps?: DatePickerPropsBase;
  onChange?(date: LocalDate): void;
  format?: FormatDateOptions;
  onClickCalendarOpen?(event: React.MouseEvent<HTMLButtonElement>): void;
  /**
   * Defaults to true. If false, the field will include IconArrowDownSmall, instead of IconCalendar.
   */
  showsCalendarIcon?: boolean;
  usePseudoElementDisplay?: boolean;
}

/**
 * Use `DatePicker` for rendering a text field with a calendar popover
 *
 * ```js
 * import { DatePickerField } from 'ui';
 * ```
 */
const DatePickerField: React.FunctionComponent<DatePickerFieldProps> = ({
  DatePickerProps,
  className,
  disabled,
  format,
  showsCalendarIcon = true,
  InputLabelProps,
  InputProps,
  inputProps,
  onChange,
  onClickCalendarOpen,
  PopoverProps,
  'aria-label': ariaLabel,
  usePseudoElementDisplay = false,
  ...rest
}) => {
  const [focused, setFocused] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | HTMLInputElement | null>(null);
  const intl = useIntl();
  const classes = useDatePickerStyles();
  const theme = useTheme();

  const isDateDisplayA11yEditEnabled = useFeatureFlagForConsumer('CHASE_A11Y_LABELS_ON_CAROUSELS');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }
    setAnchorEl(event.currentTarget);
    if (onClickCalendarOpen) {
      onClickCalendarOpen(event);
    }
  };

  const handleClickInput = (event: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectDate = (updatedDate?: LocalDate, closeCalendar?: boolean) => {
    if (!updatedDate) {
      return;
    }
    onChange?.(updatedDate);
    closeCalendar && handleClose();
  };

  const open = !!anchorEl;
  const id = open ? `date-picker-${rest.id || rest.name}` : undefined;

  const dateToFormat: LocalDate = _.isString(rest.value)
    ? LocalDate.parse(rest.value)
    : (rest.value as LocalDate);

  let value = '';
  if (dateToFormat) {
    value = intl.formatDate(toJSDate(dateToFormat), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      ...format,
      timeZone: 'UTC',
    });
  }
  const getFinalAriaLabel = () => {
    const baseLabel = rest.label;
    if (!value) {
      return baseLabel;
    }

    return `${baseLabel}, selected value is ${value}`;
  };

  const finalAriaLabel = getFinalAriaLabel();

  return (
    <>
      <div
        css={({ breakpoints }: Theme) => [
          isDateDisplayA11yEditEnabled &&
            usePseudoElementDisplay &&
            css`
              align-self: stretch;

              ${breakpoints.up('md')} {
                & .MuiFormControl-root {
                  max-width: 250px;
                  height: 100%;
                }

                & .MuiFormLabel-root.MuiInputLabel-shrink {
                  transform: translate(16px, 20px) scale(0.875) !important;
                }
              }

              // Visually hidden in favor of the ::before psuedoelement, but still included for a11y purposes.
              & input {
                clip: rect(0 0 0 0);
                clip-path: inset(50%);
                height: 1px;
                overflow: hidden;
                position: absolute;
                white-space: nowrap;
                width: 1px;
              }

              & .MuiInputBase-root {
                position: relative;
                height: max-content;
                min-height: 100%;

                // We need to support text wrapping to ensure that the entire label is visible, even when additional text
                // spacing is applied. HTML inputs do not support text-wrapping, so we display the label in a pseudo-element and
                // visually hide the input
                &::before {
                  position: static;
                  content: '${value}';
                  margin-top: 17px;
                  padding: 16px;
                  flex: 1;
                }
              }
            `,
        ]}
      >
        <TextField
          className={clsx(classes.input, className)}
          focused={focused || open}
          InputProps={{
            readOnly: true,
            onClick: handleClickInput,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  variant="ghost"
                  size="medium"
                  aria-label={finalAriaLabel as string}
                  onClick={handleClick}
                  onBlur={() => setFocused(false)}
                  onFocus={() => setFocused(true)}
                >
                  {showsCalendarIcon ? (
                    <IconMigrationUtil
                      legacyIcon={
                        <IconCalendarLegacy
                          css={(theme: Theme) => css`
                            font-size: ${theme.typography.pxToRem(20)} !important;
                          `}
                        />
                      }
                      newIcon={<IconCalendar />}
                    />
                  ) : (
                    <IconMigrationUtil
                      legacyIcon={
                        <IconArrowDownSmall
                          css={(theme: Theme) => css`
                            font-size: ${theme.typography.pxToRem(24)} !important;
                          `}
                        />
                      }
                      newIcon={<IconChevronSmallDown />}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
            inputProps: {
              disabled: true,
              ...(InputProps?.inputProps || {}),
            },
            ...InputProps,
          }}
          InputLabelProps={{
            shrink: true,
            ...InputLabelProps,
          }}
          inputProps={{ 'aria-describedby': id, 'aria-label': ariaLabel, ...inputProps }}
          disabled={disabled}
          {...rest}
          value={value}
        />
      </div>
      <Popover
        PaperProps={{
          style: {
            minWidth: theme.typography.pxToRem(268),
            padding: theme.spacing(2.5),
          },
          role: 'dialog',
          'aria-live': 'polite',
          'aria-labelledby': 'consumer-calendar-month-calendar-first',
        }}
        css={({ breakpoints }: Theme) => [
          css`
            ${breakpoints.down('xs')} {
              display: none;
            }
          `,
        ]}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        maxWidthPx={450}
        {...PopoverProps}
      >
        <DatePicker
          selectionType="single"
          selectedDate={dateToFormat}
          onDateSelected={(updatedDate) => handleSelectDate(updatedDate, true)}
          {...DatePickerProps}
        />
      </Popover>
      <Dialog
        css={({ breakpoints }: Theme) => css`
          ${breakpoints.up('sm')} {
            display: none;
          }
        `}
        open={open}
        onClose={handleClose}
        variant="partial"
      >
        <DialogHeading onClose={handleClose}>Select a date</DialogHeading>
        <DatePickerScrollableDialogContent
          DatePickerProps={{
            selectionType: 'single',
            selectedDate: dateToFormat,
            onDateSelected: (updatedDate) => handleSelectDate(updatedDate, false),
            ...DatePickerProps,
          }}
        />
        <DialogActions>
          <Button variant="primary" size="medium" fullWidth onClick={handleClose}>
            Select
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DatePickerField;
